import { app } from '@microsoft/teams-js';
import type { MeView } from '@research-assistant/shared';

import { getCachedIdentity, setCachedIdentity } from './idb.js';
import { decodeJwtPayload, ensureFreshPlatformJwt } from './lib/sso.js';
import { withTimeout } from './lib/with-timeout.js';
import { logger } from './logger.js';
import { getState } from './store/identity-store.js';

/**
 * Identity resolver — singleton. Resolves once per tab load and is consumed
 * by every API-adjacent call plus the UI.
 *
 * Resolution order (§2.2 of ct-rag-teams-ui-reusable.md, adapted):
 *   1. Teams SDK path: `app.initialize()` → `authentication.getAuthToken()`
 *      → `POST /v1/me` with `Authorization: Bearer <jwt>`.
 *   2. Dev fallback (not in Teams / local browser): `POST /v1/me` with
 *      `x-dev-user` header carrying a JSON-encoded partial JwtClaims. The
 *      server's TeamsSSOGuard accepts this in non-production.
 *   3. On network failure (both paths): return cached `MeView` from IDB
 *      with `stale: true`. UI badges "offline".
 *   4. Successful fetches always update the IDB cache before returning.
 */
export interface Identity {
  me: MeView;
  /** true when identity came from the IDB cache because the network failed. */
  stale: boolean;
}

const ME_ENDPOINT = '/v1/me';

let singleton: Promise<Identity> | null = null;

/**
 * Initialise and resolve the identity. Returns the same Promise on subsequent
 * calls until `_resetIdentityForTests` is invoked.
 */
export function initializeIdentity(): Promise<Identity> {
  if (singleton === null) {
    singleton = resolveIdentity();
  }
  return singleton;
}

/**
 * Force a re-fetch of `/v1/me`, bypassing the singleton cache. Used when
 * we need a fresh read of user-toggleable state (e.g. `prefs.showAdminLink`
 * after the user flipped it in the embedded admin-panel). Replaces the
 * cached singleton so subsequent `initializeIdentity()` calls also see the
 * refreshed value.
 */
export function refreshIdentity(): Promise<Identity> {
  const next = resolveIdentity();
  singleton = next;
  return next;
}

const TEAMS_BOOT_TIMEOUT_MS = 8000;

/**
 * Cached Teams user context from app.getContext(). Set as soon as
 * getContext() succeeds — even if getAuthToken() later times out.
 * Used by getAuthHeaders() to populate x-user-id / x-tenant-id headers.
 */
let teamsUserCtx: { userId: string; tenantId: string; email?: string } | null =
  null;

/**
 * Dedicated singleton for capturing the Teams SDK user context
 * (app.initialize → app.getContext). Resolves independently of the full
 * identity flow so getAuthHeaders() can wait specifically for the Teams
 * context without being blocked by the JWT / /v1/me round-trip.
 *
 * Root-cause fix: initializeIdentity() may resolve from the IDB cache
 * BEFORE app.getContext() sets teamsUserCtx, causing getAuthHeaders() to
 * fall back to 'dev-oid'. Separating the two concerns removes that race.
 */
let teamsCtxSingleton: Promise<void> | null = null;

function awaitTeamsCtx(): Promise<void> {
  if (teamsCtxSingleton === null) {
    teamsCtxSingleton = captureTeamsCtx();
  }
  return teamsCtxSingleton;
}

async function captureTeamsCtx(): Promise<void> {
  try {
    await withTimeout(
      app.initialize(),
      TEAMS_BOOT_TIMEOUT_MS,
      'Teams app.initialize',
    );
    const ctx = await withTimeout(
      app.getContext(),
      TEAMS_BOOT_TIMEOUT_MS,
      'Teams app.getContext',
    );
    if (ctx.user?.id && ctx.user?.tenant?.id) {
      teamsUserCtx = {
        userId: ctx.user.id,
        tenantId: ctx.user.tenant.id,
        email: ctx.user.loginHint ?? undefined,
      };
    }
  } catch (err: unknown) {
    logger.debug('Teams context unavailable', err);
  }
}

/** Test-only helper — retained for source-compat. No token cache to clear. */
export function _resetTokenCacheForTests(): void {
  /* platform JWT lives in identity-store; reset it via _resetIdentityStoreForTests */
}

async function resolveIdentity(): Promise<Identity> {
  // Capture Teams context (best-effort) so getAuthHeaders() can still populate
  // legacy x-user-* headers when running inside a Teams Tab. The gateway strips
  // those anyway — identity travels as the platform JWT bearer.
  await awaitTeamsCtx();

  const stored = getState().platformJwt;
  // No platform JWT yet — standalone browser before sign-in. Leave identity
  // unresolved (AuthGate shows the sign-in screen); the Teams Tab obtains the
  // JWT via lib/sso.ts before the app mounts.
  if (stored === null || stored.length === 0) {
    const cached = await getCachedIdentity();
    if (cached !== null) {
      return { me: cached, stale: true };
    }
    throw new Error('No platform JWT — sign-in required');
  }

  // --- Fetch /v1/me with the platform JWT. -------------------------------
  try {
    const me = await fetchMe(await freshStoredJwt(stored));
    await setCachedIdentity(me);
    return { me, stale: false };
  } catch (err: unknown) {
    logger.warn('Network fetch of /v1/me failed; falling back to cache', err);
    const cached = await getCachedIdentity();
    if (cached !== null) {
      return { me: cached, stale: true };
    }
    throw err;
  }
}

/**
 * The stored JWT, renewed first when it has aged out. A tab reopened after more than a quarter of
 * an hour holds an expired token, and `/v1/me` sent with it would answer 401 and leave the app on
 * the cached identity, roles and all. A renewal that fails hands back the stored one.
 */
async function freshStoredJwt(stored: string): Promise<string> {
  try {
    return (await ensureFreshPlatformJwt()) ?? stored;
  } catch {
    return stored;
  }
}

async function fetchMe(jwt: string | null): Promise<MeView> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (jwt !== null && jwt.length > 0) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }

  const response = await fetch(ME_ENDPOINT, {
    method: 'GET',
    headers,
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`GET ${ME_ENDPOINT} returned ${response.status}`);
  }

  const body = (await response.json()) as unknown;
  if (!isMeView(body)) {
    throw new Error(`GET ${ME_ENDPOINT} returned malformed MeView payload`);
  }
  return body;
}

function isMeView(value: unknown): value is MeView {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record['userId'] === 'string' &&
    typeof record['tenantId'] === 'string' &&
    typeof record['displayName'] === 'string' &&
    Array.isArray(record['roles']) &&
    record['roles'].every((role) => typeof role === 'string')
  );
}

/**
 * Build auth headers for quick-actions / feedback / lib-api endpoints.
 *
 * Token model: every call authenticates with the platform JWT bearer, which
 * the gateway and services verify against cti-identity-service's JWKS. We wait
 * for initializeIdentity() so the platform JWT has been resolved/stored before
 * reading it. Any client-supplied identity headers are stripped by the gateway.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    await initializeIdentity();
  } catch {
    // Offline / not-signed-in — proceed best-effort; the call may 401.
  }
  // This silently renews the platform JWT when it is missing or near expiry:
  // the Teams SSO token in a Tab, the signed-in Entra account in a browser.
  const jwt = await ensureFreshPlatformJwt();
  return jwt !== null && jwt.length > 0
    ? { Authorization: `Bearer ${jwt}` }
    : {};
}

/**
 * The signed-in caller's own email, resolved across every sign-in mode. Reads
 * the platform JWT's `email` claim first — identity stamps it for Teams SSO,
 * Entra, and dev logins alike — and falls back to the Teams tab's loginHint.
 * Returns null when no email is resolvable (e.g. a JWT minted without the
 * claim). Used to stamp a "ping yourself" reminder's recipient with the
 * caller's own address so the Secretary resolves it via Graph like any other.
 */
export function getCallerEmail(): string | null {
  const jwt = getState().platformJwt;
  if (jwt !== null && jwt.length > 0) {
    const claims = decodeJwtPayload(jwt);
    const email = claims === null ? undefined : claims['email'];
    if (typeof email === 'string' && email.trim().length > 0) {
      return email.trim();
    }
  }
  const loginHint = teamsUserCtx?.email;
  if (typeof loginHint === 'string' && loginHint.trim().length > 0) {
    return loginHint.trim();
  }
  return null;
}

/**
 * The signed-in caller's platform-JWT `sub` claim, or null when unresolvable. This is exactly the
 * value cti-quality-service stamps as a regression job's `created_by` (both derive from the same
 * JWKS-verified bearer), so the Regression tab compares against it to gate creator-only controls.
 * The gate is defense-in-depth — the backend 403s a non-creator regardless.
 */
export function getCallerSub(): string | null {
  const jwt = getState().platformJwt;
  if (jwt !== null && jwt.length > 0) {
    const claims = decodeJwtPayload(jwt);
    const sub = claims === null ? undefined : claims['sub'];
    if (typeof sub === 'string' && sub.trim().length > 0) {
      return sub.trim();
    }
  }
  return null;
}

/**
 * The signed-in caller's platform-JWT `oid` claim (Azure AD object id), or null when the user has no
 * linked directory identity (e.g. a dev email login). This is exactly the value the conversation
 * service stamps as a message's `sender_user_id` for SSO callers (`agentUserId = oid ?? sub`), so a
 * collaborative thread compares against it — plus `getCallerSub()` for dev logins — to tell the
 * caller's own messages from other members'.
 */
export function getCallerOid(): string | null {
  const jwt = getState().platformJwt;
  if (jwt !== null && jwt.length > 0) {
    const claims = decodeJwtPayload(jwt);
    const oid = claims === null ? undefined : claims['oid'];
    if (typeof oid === 'string' && oid.trim().length > 0) {
      return oid.trim();
    }
  }
  return null;
}

/** Test-only helper to clear the singleton between runs. */
export function _resetIdentityForTests(): void {
  singleton = null;
  teamsUserCtx = null;
  teamsCtxSingleton = null;
}
